import React, {useEffect, useState, useRef} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {selectAccessToken, selectAuthenticated, selectUserData} from "../selectors";
import Common from "../components/Onboarding/Common";
import { AGENT_HOME_ENABLED, TRYON_SERVER_URL } from "../config";
import { fetchUserOnboardingStatus } from "../onboarding/utils/onboardingApi";
import { fetchAllImages } from "../Utils/ExperimentImageUtils";
import axios from "axios";
import { Button } from "flowbite-react";
import ThreeDotSpinner from "../components/ThreeDotSpinner";
import SignInPrompt from "../components/SignInPrompt";
import { ArrowLeftRightIcon, BrainIcon, FlipHorizontal2Icon, ImageIcon, ShirtIcon, UserIcon, WandSparklesIcon, X } from "lucide-react";
import HomeAgentPrompt from "../agent/HomeAgentPrompt";

export default function Playground(props) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = useRef(null);
    const onboardingFetchInFlightRef = useRef(false);
    const authenticated = useSelector(selectAuthenticated);
    const user = useSelector(selectUserData);
    const [showBasicInfoModal, setShowBasicInfoModal] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorStatus, setErrorStatus] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const accessToken = useSelector(selectAccessToken)
    const [showBusinessTypeModal, setShowBusinessTypeModal] = useState(true);
    const [showProductCategoryModal, setShowProductCategoryModal] = useState(false);
    const [showECommercePlatformModal, setShowECommercePlatformModal] = useState(false);
    const [showLookingForModal, setShowLookingForModal] = useState(false);
    const [showBusinessGoalModal, setShowBusinessGoalModal] = useState(false);

    const [showThankYouModal, setShowThankYouModal] = useState(false);
    const [checkingOnboarding, setCheckingOnboarding] = useState(false);
    const [recentImages, setRecentImages] = useState([]);
    const [recentLoaded, setRecentLoaded] = useState(false);
    const [examples, setExamples] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);

    const businessTypes = ["E-Commerce Company", "D2C Brand", "Fashion Designer", "App Developer", "Other(Specify)"];
    const businessGoals = ["Enhance User Experience", "Improve Design Visualization", "Increase Conversion Rates", "Reduce Return Rates", "Streamline Design-to-Production Process", "Improve Catalog Management Efficiency", "Stay Competitive in the Market", "Other(Specify)"];
    const productCategories = ["Clothing", "Shoes", "Jewelry", "Accessories", "Eyewear", "Makeup", "Home Furnishings", "Other(Specify)"];
    const eCommercePlatforms = ["Shopify", "Magento", "WooCommerce", "BigCommerce", "Other(Specify)"];
    const lookingFor = ["Virtual Try-On", "Model Swap", "Outfit Generation", "Outfit Caption", "Other(Specify)"];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth >= 768) {
                setSidebarOpen(true);
            }else{
                setSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);

        // Initial check
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleClickOutside = (event) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
            if (isMobile) {
                toggleSidebar(false);
            }
        }
    };

    useEffect(() => {
        if (isSidebarOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSidebarOpen]);

    // If user has no onboarding steps, send them to the new onboarding screen (single source of truth).
    // Skip fetch when we just came from /onboarding (redirect with completed) to avoid duplicate API calls.
    useEffect(() => {
        if (!authenticated || !accessToken) return;
        if (location.state?.onboardingAlreadyChecked) {
            setCheckingOnboarding(false);
            navigate(".", { replace: true, state: {} });
            return;
        }
        if (onboardingFetchInFlightRef.current) return;
        let cancelled = false;
        onboardingFetchInFlightRef.current = true;
        setCheckingOnboarding(true);
        fetchUserOnboardingStatus(accessToken)
            .then((result) => {
                if (cancelled) return;
                if (result.status !== "success" || result.isCompleted) return;
                if (localStorage.getItem("OnboardingSkipped") === "true") return;
                navigate("/onboarding", { replace: true });
            })
            .finally(() => {
                onboardingFetchInFlightRef.current = false;
                if (!cancelled) setCheckingOnboarding(false);
            });
        return () => {
            cancelled = true;
            setCheckingOnboarding(false);
        };
    }, [authenticated, accessToken, navigate]);

    useEffect(() => {
        if (authenticated === null) return;
        if (!authenticated || !accessToken) {
            setRecentImages([]);
            setRecentLoaded(true);
            return;
        }
        let cancelled = false;
        fetchAllImages(
            `${TRYON_SERVER_URL}/api/v1/experiment_image/?`,
            accessToken,
            true,
            false,
            { limit: 8 }
        ).then((response) => {
            if (cancelled) return;
            setRecentImages(response?.status === "success" && Array.isArray(response.data) ? response.data : []);
            setRecentLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [accessToken, authenticated]);

    useEffect(() => {
        if (!recentLoaded || recentImages.length > 0) return;
        let cancelled = false;
        fetchAllImages(
            `${TRYON_SERVER_URL}/api/v1/experiment_image/?`,
            null,
            false,
            true,
            { limit: 3 }
        ).then((response) => {
            if (cancelled) return;
            setExamples(response?.status === "success" && Array.isArray(response.data) ? response.data.slice(0, 3) : []);
        });
        return () => {
            cancelled = true;
        };
    }, [recentLoaded, recentImages.length]);

    const toggleSidebar = () => {
        setSidebarOpen(choice => !choice);
    };

    const showErrorModal = (status, message) => {
        setShowError(true);
        setErrorStatus(status);
        setErrorMessage(message);
    }

    const handleErrorClose = () => {
        setShowError(false);
    }

    const handleContinue = (option, value) => {
        console.log("Continue", option, value);
        if(option === "Business Type"){
            setShowBusinessTypeModal(false);
            setShowBusinessGoalModal(true);
        }else if(option === "Business Goal"){
            setShowBusinessGoalModal(false);
            setShowProductCategoryModal(true);  
        }else if(option === "Product Category"){
            setShowProductCategoryModal(false);
            setShowECommercePlatformModal(true);
        }else if(option === "E-Commerce Platform"){
            setShowECommercePlatformModal(false);
            setShowLookingForModal(true);
        }else if(option === "Looking For"){
            setShowLookingForModal(false);
            setShowThankYouModal(true);
        }

        if(option === "Business Type"){ 
            updateUserOnboardingData({"onboarding_step": "business_type", 
                "onboarding_value": value});
        }else if(option === "Business Goal"){
            updateUserOnboardingData({"onboarding_step": "business_goal", 
                "onboarding_value": value});
        }else if(option === "Product Category"){
            updateUserOnboardingData({"onboarding_step": "product_category", 
                "onboarding_value": value});
        }else if(option === "E-Commerce Platform"){
            updateUserOnboardingData({"onboarding_step": "ecommerce_platform", 
                "onboarding_value": value});
        }else if(option === "Looking For"){
            updateUserOnboardingData({"onboarding_step": "looking_for", 
                "onboarding_value": value});
        }
    }

    const handleSkip = (option) => {
        console.log("Skip", option);
        setShowBasicInfoModal(false);
        localStorage.setItem("OnboardingSkipped", true);
    }

    async function updateUserOnboardingData(data){
        try{
            axios.post(`${TRYON_SERVER_URL}/api/v1/user/onboarding/`, data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            }).then((response) => {
                console.log("Update User Onboarding Data Response:", response);
                // alert("User onboarding data updated successfully");
                setShowError(false);
                setErrorStatus("");
                setErrorMessage("");
            }).catch((error) => {
                console.error('Error updating user onboarding data:', error);
                //alert("Failed to update user onboarding data");
                setShowError(true);
                setErrorStatus("Error");
                setErrorMessage("Error updating user onboarding data");
            });
        } catch (error) {
            console.error('Error updating user onboarding data:', error);
            // alert("Error updating user onboarding data");
            setShowError(true);
            setErrorStatus("Error");
            setErrorMessage("Error updating user onboarding data");
        }
    }

    function handleSelectOption(option) {
        console.log("handleSelectOption inside sidebar: ", option)
        let options1 = props.options;
        console.log("options1 before", options1)
        Object.keys(options1).forEach((key) => {
            if (key === option) {
                options1[key] = true;
            } else {
                options1[key] = false;
            }
        })
        console.log("options1 after", options1)
        props.handleOptionSelect(option, options1);
    }

    const tools = [
        { name: "Try on", icon: <ShirtIcon className="h-6 w-6" />, path: "/tryonai" },
        { name: "Reimagine", icon: <WandSparklesIcon className="h-6 w-6" />, path: "/reimagineai" },
        { name: "Generate", icon: <BrainIcon className="h-6 w-6" />, path: "/generate" },
        { name: "Studio", icon: <FlipHorizontal2Icon className="h-6 w-6" />, path: "/tryonstudio" },
        { name: "Models", icon: <UserIcon className="h-6 w-6" />, path: "/model-generator" },
        { name: "Swap", icon: <ArrowLeftRightIcon className="h-6 w-6" />, path: "/aimodel" },
    ];

    const exampleRoutes = ["/tryonai", "/reimagineai", "/generate"];
    const exampleNames = ["Try on", "Reimagine", "Generate"];

    return (
        <div>
            {/* Global loader while checking onboarding status */}
            {checkingOnboarding && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ground">
                    <div className="text-center">
                        <ThreeDotSpinner />
                        <p className="mt-4 font-Mulish text-sm text-graphite">Loading…</p>
                    </div>
                </div>
            )}

            <div className="h-[calc(100vh-5rem)] overflow-y-auto bg-ground px-4 py-8 sm:px-6">
                <div className="mx-auto max-w-5xl">
                    <h1 className="font-newsreader text-4xl font-medium tracking-tight text-secondary">
                        What do you want to make?
                    </h1>
                    <p className="mt-2 font-Mulish text-sm text-graphite">
                        Try a garment on, change a photo, or generate a look.
                    </p>

                    {AGENT_HOME_ENABLED && <HomeAgentPrompt />}

                    <div className="mt-6 grid grid-cols-3 gap-2 rounded-2xl bg-white p-3 shadow-[0_0_0_1px_var(--hairline)] sm:grid-cols-6">
                        {tools.map((tool) => (
                            <button
                                key={tool.path}
                                type="button"
                                onClick={() => handleSelectOption(tool.path)}
                                className="flex flex-col items-center gap-2 rounded-xl px-2 py-3 text-secondary hover:bg-primary-50"
                            >
                                {tool.icon}
                                <span className="font-Mulish text-sm">{tool.name}</span>
                            </button>
                        ))}
                    </div>

                    <h2 className="mb-3 mt-8 font-Mulish text-lg font-semibold text-secondary">Recent</h2>
                    {!recentLoaded ? (
                        <div className="flex justify-center py-8">
                            <ThreeDotSpinner />
                        </div>
                    ) : recentImages.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {recentImages.map((image) => (
                                <button
                                    key={image.id}
                                    type="button"
                                    onClick={() => setPreviewImage(image)}
                                    className="overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_var(--hairline)]"
                                >
                                    <img
                                        src={`${TRYON_SERVER_URL}/${image.image_url}`}
                                        alt=""
                                        className="aspect-[3/4] w-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    ) : authenticated !== true ? (
                        <>
                            <SignInPrompt
                                title="Sign in to see your photos"
                                message="Your try-ons and generated photos will show up here."
                                onSignIn={() => navigate("/signin")}
                            />
                            {examples.length > 0 && (
                                <>
                                    <h2 className="mb-3 mt-8 font-Mulish text-lg font-semibold text-secondary">
                                        Start from an example
                                    </h2>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        {examples.map((image, index) => (
                                            <div
                                                key={image.id}
                                                className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]"
                                            >
                                                <img
                                                    src={`${TRYON_SERVER_URL}/${image.image_url}`}
                                                    alt=""
                                                    className="aspect-[3/4] w-full object-cover"
                                                />
                                                <div className="flex items-center justify-between p-3">
                                                    <span className="font-Mulish text-sm font-semibold text-secondary">
                                                        {exampleNames[index] || "Photo"}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectOption(exampleRoutes[index] || "/tryonai")}
                                                        className="rounded-full border border-primary-200 bg-white px-3 py-1.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                                                    >
                                                        Try this
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="rounded-2xl bg-primary-50 px-6 py-10 text-center">
                                <ImageIcon className="mx-auto mb-3 h-8 w-8 text-primary-700" />
                                <div className="font-Mulish text-lg font-semibold text-secondary">No results yet</div>
                                <p className="mt-1 font-Mulish text-sm text-graphite">
                                    A try-on, edit, or generated photo will show up here.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => handleSelectOption("/tryonai")}
                                    className="mt-4 rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                >
                                    Try something on
                                </button>
                            </div>
                            {examples.length > 0 && (
                                <>
                                    <h2 className="mb-3 mt-8 font-Mulish text-lg font-semibold text-secondary">
                                        Start from an example
                                    </h2>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        {examples.map((image, index) => (
                                            <div
                                                key={image.id}
                                                className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]"
                                            >
                                                <img
                                                    src={`${TRYON_SERVER_URL}/${image.image_url}`}
                                                    alt=""
                                                    className="aspect-[3/4] w-full object-cover"
                                                />
                                                <div className="flex items-center justify-between p-3">
                                                    <span className="font-Mulish text-sm font-semibold text-secondary">
                                                        {exampleNames[index] || "Photo"}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectOption(exampleRoutes[index] || "/tryonai")}
                                                        className="rounded-full border border-primary-200 bg-white px-3 py-1.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                                                    >
                                                        Try this
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {previewImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="relative max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => setPreviewImage(null)}
                            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                        >
                            ×
                        </button>
                        <img
                            src={`${TRYON_SERVER_URL}/${previewImage.image_url}`}
                            alt=""
                            className="max-h-[85vh] w-auto object-contain"
                        />
                    </div>
                </div>
            )}

            {/* Basic Info Modal */}
            {showBasicInfoModal && authenticated ? (
            <div className="fixed left-0 top-0 z-50 h-full w-full overflow-y-auto bg-overlay">
                <div className="relative mx-4 my-10 h-auto max-w-xl rounded-2xl bg-white p-6 text-left shadow-[0_0_0_1px_var(--hairline)] lg:mx-auto">
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowBasicInfoModal(false);
                        }}
                        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_var(--hairline)]"
                    >
                        <X className="h-4 w-4" aria-label="Close" />
                    </button>

                    <p className="font-Mulish text-lg font-semibold text-secondary">Welcome to TryOn AI</p>

                    {showThankYouModal ? (
                        <div className="mt-4 text-center font-Mulish text-sm text-graphite">Thanks. The playground is ready.</div>
                    ):(
                        <div className="mt-4 font-Mulish text-sm text-graphite">Tell us a bit about your work so the playground can start in the right place.</div>
                    )}

                    {showBusinessTypeModal && (
                        <Common title="Business Type" multipleSelect={false} options={businessTypes} handleContinue={handleContinue} handleSkip={handleSkip} showErrorModal={showErrorModal} />
                    )}

                    {showBusinessGoalModal && ( 
                        <Common title="Business Goal" multipleSelect={true} options={businessGoals} handleContinue={handleContinue} handleSkip={handleSkip} showErrorModal={showErrorModal} />
                    )}

                    {showProductCategoryModal && (
                        <Common title="Product Category" multipleSelect={true} options={productCategories} handleContinue={handleContinue} handleSkip={handleSkip} showErrorModal={showErrorModal} />
                    )}

                    {showECommercePlatformModal && (
                        <Common title="E-Commerce Platform" multipleSelect={true} options={eCommercePlatforms} handleContinue={handleContinue} handleSkip={handleSkip} showErrorModal={showErrorModal} />
                    )}

                    {showLookingForModal && (
                        <Common title="Looking For" multipleSelect={true} options={lookingFor} handleContinue={handleContinue} handleSkip={handleSkip} showErrorModal={showErrorModal} />
                    )}

                    {showThankYouModal && (
                        <div className="flex flex-col items-center justify-center">
                            <Button className="mt-4 rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white"
                            onClick={() => {
                                setShowThankYouModal(false);
                                setShowBasicInfoModal(false);
                            }}
                            >
                                Open Playground
                            </Button>
                    </div>
                    
                    )}
                </div>

                {showError && (
                <div id="toast-default" class="fixed bottom-5 left-1/2 transform -translate-x-1/2 flex items-center w-full max-w-lg p-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800" role="alert">
                    <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-red-500 bg-red-100 rounded-lg dark:bg-red-800 dark:text-red-200">
                        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
                        </svg>
                        <span class="sr-only">Error icon</span>
                    </div>
                    <div class="flex flex-col">
                    <div class="ms-3 text-base font-normal text-red-500">{errorStatus}</div>
                    <div class="ms-3 text-sm font-normal text-red-400">{errorMessage}</div>
                    </div>
                    <button type="button" class="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700" data-dismiss-target="#toast-default" aria-label="Close" onClick={handleErrorClose}>
                        <span class="sr-only">Close</span>
                        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                    </button>
                </div>
                )}
            </div>
            ):(<div></div>)}
        </div>
    )
}